import { DataStore } from '../../data/store';
import { ContributionKpiConfig, KpiDecompositionInfo } from './types';
import { error } from '@ecosystem/newron-client-lib';
import { ChartColors } from '../../design/charts';
import { DataColumn, DataRow, KpiWithChangeColumn } from '../../types';
import { Formatting } from '../../data/formatting';
import { getChangeColor } from '../data/common';
import { DecompositionChartData } from '../../design/charts/types';

const formatValue = (value: number, column: DataColumn, currency) =>
  Formatting.format(value, {
    dataType: column.dataType,
    formatHint: column.formatHint,
    isChange: false,
    currency,
    hyphenForNull: true,
  });

const formatChange = (value: number, column: DataColumn, currency) =>
  Formatting.format(value, {
    dataType: column.dataType,
    formatHint: column.formatHint,
    isChange: true,
    currency,
    hyphenForNull: true,
  });

export function makeDecompositionInfo(
  data: DataStore,
  options: {
    decomposedKpi: string;
    contributionKpis: ContributionKpiConfig[];
  }
): KpiDecompositionInfo | undefined {
  const { decomposedKpi, contributionKpis } = options;

  const kpiColumn = data.getDataColumn(decomposedKpi);
  const kpiChangeColumn = data.getDataColumn((kpiColumn as KpiWithChangeColumn)?.changeName);
  if (!kpiColumn || !kpiChangeColumn) {
    return;
  }

  const columns = data.getDataColumns();
  const missingKpis = contributionKpis
    .flatMap(kpi => [kpi.kpi, kpi.kpiContribution])
    .filter(kpi => !columns.some(col => col.name === kpi));

  if (missingKpis.length > 0) {
    error(new Error(`Some of the kpis for the kpi decomposition are missing: ${missingKpis.join(', ')}`));
    return;
  }

  const resultSets = data.getGroupedSets();
  const dataRows = resultSets?.[0]?.data;
  const dataRow = dataRows?.[0];
  if (resultSets.length !== 1 || dataRows?.length !== 1) {
    error(
      new Error(
        `Kpi decomposition must have only one grouped result with only one data row (received ${resultSets.length} results with ${dataRows?.length} rows in the first one)`
      )
    );
    return;
  }
  const labels = resultSets[0].type.timeFrame.periods.map(p => p.name);
  const currency = data.getCurrency();
  const rawValues = dataRow.values[kpiColumn.name] as number[];
  const changeValues = dataRow.values[kpiChangeColumn.name] as number[];

  const formattedValues = rawValues.map(value => formatValue(value, kpiColumn, currency));
  const formattedChangeValues = changeValues.map(value => formatChange(value, kpiChangeColumn, currency));

  const contributingKpiInfo = mapToContributingKpiInfo(data, dataRow, kpiColumn, contributionKpis);
  const isKpiQualityOk = !contributingKpiInfo.some(v => +v.floatingLabel.topLabel[0].value.replace(/\D/g, '') === 0);

  const chartData: DecompositionChartData[] = [
    {
      xLabel: labels[0],
      floatingLabel: {
        topLabel: [{ value: formattedValues[0], textColor: ChartColors.neutral, bold: true }],
      },
      data: [0, rawValues[0]],
      fillColor: ChartColors.neutral,
    },
    ...contributingKpiInfo,
    {
      xLabel: labels[1],
      floatingLabel: {
        topLabel: [
          { value: formattedValues[1], textColor: ChartColors.neutral, bold: true },
          { value: formattedChangeValues[1], textColor: getChangeColor(changeValues[1]) },
        ],
      },
      data: [0, rawValues[1]],
      fillColor: ChartColors.neutral,
    },
  ];

  return {
    info: chartData,
    kpiChange: changeValues[1],
    labels: labels,
    kpiChangeFormatted: formattedChangeValues[1],
    isKpiQualityOk,
  };
}

function mapToContributingKpiInfo(
  data: DataStore,
  dataRow: DataRow,
  decomposedKpi: DataColumn,
  contributionKpis: ContributionKpiConfig[]
): DecompositionChartData[] {
  const decomposedKpiRawValues = dataRow.values[decomposedKpi.name] as number[];
  let seed = decomposedKpiRawValues[0];

  const decompositionConfig: DecompositionChartData[] = [];

  contributionKpis.forEach((contributionKpiConfig: ContributionKpiConfig) => {
    const config = mapToChartConfigData(data, dataRow, contributionKpiConfig, seed);
    decompositionConfig.push(config);
    seed = config.data[1];
  });

  return decompositionConfig;
}

function mapToChartConfigData(
  data: DataStore,
  dataRow: DataRow,
  contributionKpi: ContributionKpiConfig,
  seed: number
): DecompositionChartData {
  const currency = data.getCurrency();

  const kpiColumn = data.getDataColumn(contributionKpi.kpi);
  const kpiChangeColumn = data.getDataColumn((kpiColumn as KpiWithChangeColumn).changeName);
  const kpiContributionColumn = data.getDataColumn(contributionKpi.kpiContribution);

  const rawValues = dataRow.values[kpiColumn.name] as number[];
  const changeValues = dataRow.values[kpiChangeColumn.name] as number[];
  const contributionValues = dataRow.values[kpiContributionColumn.name] as number[];

  const formattedKpiValue = formatValue(rawValues[1], kpiColumn, currency);
  const formattedChangeValue = formatChange(changeValues[1], kpiChangeColumn, currency);

  const contributionValue = contributionValues[1];
  const formattedContributionValue = formatChange(contributionValues[1], kpiContributionColumn, currency);

  return {
    xLabel: '',
    floatingLabel: {
      topLabel: [{ value: formattedContributionValue, textColor: getChangeColor(contributionValue), bold: true }],
      bottomLabel: [
        { value: kpiColumn.label, textColor: ChartColors.text },
        { value: formattedKpiValue, textColor: ChartColors.text },
        { value: formattedChangeValue, textColor: getChangeColor(changeValues[1]) },
      ],
    },
    data: [seed, seed + contributionValue],
    fillColor: getChangeColor(contributionValue),
  };
}
